<template>
  <ion-page>
    <ion-content v-if="!navigating" :scroll-y="true" class="bg-[#F6F4F0]">
      <div class="min-h-full flex flex-col px-[24px]">
        <BackButton class="w-full mb-[32px]" :customBack="back" />
        <div class="mb-[40px]">
          <h1 class="text-[2rem] font-black text-[#433D31] mb-[8px] tracking-[-0.03em]">
            {{ headerText }}
          </h1>
          <p class="font-semibold text-[1rem] leading-[1.5rem] text-[#433D31]">
            {{ helperText }}
          </p>
        </div>

        <div class="flex-1">
          <PinInput
            :key="`pin-step-${step}`"
            ref="pinInput"
            v-model="pin"
            :length="PIN_LENGTH"
            :auto-submit="true"
            :disabled="loading"
            :has-error="!!errorMsg"
            @complete="handlePinComplete"
          />

          <p v-if="errorMsg" class="text-center text-[0.875rem] text-red-500 mt-4" role="alert">
            {{ errorMsg }}
          </p>
        </div>

        <div class="pb-[40px] pt-[20px]">
          <button
            @click="handleSubmit"
            class="w-full py-[16px] rounded-full font-semibold text-[1rem] transition-all"
            :class="
              canSubmit
                ? 'bg-[#42362B] text-white active:scale-[0.98]'
                : 'bg-[#C3C2C1] text-[#FFFFFF] cursor-not-allowed'
            "
            :disabled="!canSubmit"
          >
            {{ submitButtonText }}
          </button>

          <button
            @click="handleCancel"
            class="w-full mt-3 py-[16px] text-[#42362B] font-medium disabled:opacity-50"
            :disabled="loading"
          >
            Cancel
          </button>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { IonPage, IonContent } from '@ionic/vue';
import PinInput from '@/components/MyGiving/Common/PinInput.vue';
import BackButton from '@/components/common/BackButton.vue';

const PIN_LENGTH = 4;
const STEP_CREATE = 1;
const STEP_CONFIRM = 2;

const WEAK_PIN_PATTERNS = {
  REPEATED: /^(\d)\1+$/,
  ASCENDING: /^0?1?2?3?4?5?6?7?8?9$/,
  DESCENDING: /^9?8?7?6?5?4?3?2?1?0$/,
};

export default {
  name: 'CreatePinPage',

  components: {
    BackButton,
    IonPage,
    IonContent,
    PinInput,
  },

  data() {
    return {
      step: STEP_CREATE,
      pin: '',
      firstPin: null,
      errorMsg: '',
      loading: false,
      navigating: false,
    };
  },

  computed: {
    PIN_LENGTH() {
      return PIN_LENGTH;
    },

    isComplete() {
      return this.pin.length === PIN_LENGTH;
    },

    canSubmit() {
      return this.isComplete && !this.loading;
    },

    headerText() {
      return this.step === STEP_CREATE ? 'Create your PIN' : 'Confirm PIN';
    },

    helperText() {
      return this.step === STEP_CREATE
        ? 'Set a 4-digit PIN to keep your giving records secure. Something easy to remember — but not too obvious, yeah? 😉'
        : 'Enter PIN again to confirm.';
    },

    submitButtonText() {
      if (this.loading) return 'Processing...';
      return this.step === STEP_CREATE ? 'Continue' : 'Create PIN';
    },
  },

  methods: {
    validatePin(pin) {
      if (WEAK_PIN_PATTERNS.REPEATED.test(pin)) {
        return {
          valid: false,
          message: 'PIN cannot be all the same digits (e.g., 1111). Please choose a stronger PIN.',
        };
      }

      if (this.isAscendingSequence(pin)) {
        return {
          valid: false,
          message:
            'PIN cannot be an ascending sequence (e.g., 1234). Please choose a stronger PIN.',
        };
      }

      if (this.isDescendingSequence(pin)) {
        return {
          valid: false,
          message:
            'PIN cannot be a descending sequence (e.g., 4321). Please choose a stronger PIN.',
        };
      }

      return { valid: true };
    },

    isAscendingSequence(pin) {
      for (let i = 0; i < pin.length - 1; i++) {
        const current = parseInt(pin[i]);
        const next = parseInt(pin[i + 1]);

        // Check if next digit is exactly 1 more than current
        if (next !== current + 1) {
          return false;
        }
      }
      return true;
    },

    isDescendingSequence(pin) {
      for (let i = 0; i < pin.length - 1; i++) {
        const current = parseInt(pin[i]);
        const next = parseInt(pin[i + 1]);

        if (next !== current - 1) {
          return false;
        }
      }
      return true;
    },

    // 🔧 NEW: Handle auto-submit from PinInput
    handlePinComplete(value) {
      this.pin = value;
      // Auto-submit when PIN is complete
      this.$nextTick(() => {
        if (this.canSubmit) {
          this.handleSubmit();
        }
      });
    },

    async handleSubmit() {
      if (!this.canSubmit) return;

      const currentPin = this.pin;

      // Step 1: Validate and save first PIN
      if (this.step === STEP_CREATE) {
        // 🔧 NEW: Validate PIN strength
        const validation = this.validatePin(currentPin);

        if (!validation.valid) {
          this.showError(validation.message);
          this.pin = '';
          this.$nextTick(() => {
            this.$refs.pinInput?.focusFirst?.();
          });
          return;
        }

        this.firstPin = currentPin;
        this.moveToConfirmStep();
        return;
      }

      if (currentPin !== this.firstPin) {
        this.showError('PIN does not match. Try again.');
        this.resetToCreateStep();
        return;
      }

      await this.createPin(currentPin);
    },

    moveToConfirmStep() {
      this.step = STEP_CONFIRM;
      this.errorMsg = '';
      this.pin = '';
      this.$nextTick(() => {
        this.$refs.pinInput?.focusFirst?.();
      });
    },

    resetToCreateStep() {
      this.step = STEP_CREATE;
      this.firstPin = null;
      this.pin = '';
      this.$nextTick(() => {
        this.$refs.pinInput?.focusFirst?.();
      });
    },

    // 🔧 REFACTORED: Extracted PIN creation logic
    async createPin(pin) {
      this.loading = true;
      this.errorMsg = '';

      try {
        const res = await this.$store.dispatch('myGiving/createPin', { pin });

        const data = res?.data || res;

        if (
          data?.state === 'pin_created' ||
          data?.state === 'pin_updated' ||
          data?.state === 'pin_exists'
        ) {
          this.navigating = true;
          await this.$nextTick();

          // Back to the landing, which asks for the new PIN and then opens any
          // pending destination (only a MyGiving page, as the landing checks too).
          this.$router.replace({ path: '/my-giving', query: this.pendingRedirectQuery() });
        } else {
          this.showError('Failed to create PIN. Please try again.');
          this.resetToCreateStep();
        }
      } catch (error) {
        if (error.response?.status === 400) {
          this.showError('Invalid PIN format. Please try again.');
        } else if (error.response?.status === 409) {
          this.showError('PIN already exists. Please contact support.');
        } else {
          this.showError('Something went wrong. Please try again.');
        }

        this.resetToCreateStep();
      } finally {
        this.loading = false;
      }
    },

    pendingRedirectQuery() {
      const { redirect } = this.$route.query;
      const safe =
        typeof redirect === 'string' &&
        redirect.startsWith('/my-giving/') &&
        !redirect.startsWith('/my-giving/pin/');
      return safe ? { redirect } : {};
    },

    showError(message) {
      this.errorMsg = message;
    },

    back() {
      if (this.loading) return;

      if (this.step === STEP_CONFIRM) {
        this.resetToCreateStep();
      } else {
        this.navigateBack();
      }
    },

    handleCancel() {
      if (this.loading) return;
      this.navigateBack();
    },

    navigateBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.replace('/my-giving');
      }
    },
  },
};
</script>

<style scoped>
ion-content {
  --background: #f6f4f0;
}

ion-content::part(scroll) {
  padding-bottom: env(safe-area-inset-bottom);
}
</style>
